import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
import { AppShell, ROLES } from './components/AppShell';
import { ConsentGate } from './components/ConsentGate';
import { PrivacyNoticePage } from './pages/PrivacyNoticePage';
import { api, resolveTenantSlug, tokens } from './lib/api';
import { PublicSiteGate } from './pages/PublicSitePage';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { TermsPage, PrivacyPage, DeleteAccountPage } from './pages/LegalPages';
import { OverviewPage } from './pages/OverviewPage';
import { AskEduvoraPage } from './pages/AskEduvoraPage';
import { StudentsAtRiskPage } from './pages/StudentsAtRiskPage';
import { ClassResultsPage } from './pages/ClassResultsPage';
import { PresencePage } from './pages/PresencePage';
import { ExamEligibilityPage } from './pages/ExamEligibilityPage';
import { StaffOverviewPage } from './pages/StaffOverviewPage';
import { InvigilationPage } from './pages/InvigilationPage';
import { QuestionBankPage } from './pages/QuestionBankPage';
import { SourcesPage } from './pages/SourcesPage';
import { PaperBuilderPage } from './pages/PaperBuilderPage';
import { BlueprintEditorPage } from './pages/BlueprintEditorPage';
import { MarkEntryPage } from './pages/MarkEntryPage';
import { MasteryPage } from './pages/MasteryPage';
import { UsagePage } from './pages/UsagePage';
import { useSearchParams } from 'react-router-dom';
import { MyDutiesPage } from './pages/MyDutiesPage';
import { StudentsPage } from './pages/StudentsPage';
import { AdmissionsPage } from './pages/AdmissionsPage';
import { AdmissionsFunnelPage } from './pages/AdmissionsFunnelPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { FeedPage } from './pages/FeedPage';
import { LibraryPage } from './pages/LibraryPage';
import { HostelPage } from './pages/HostelPage';
import { DriverPage } from './pages/DriverPage';
import { VerifyPage } from './pages/VerifyPage';
import { GatePage } from './pages/GatePage';
import { OfficeReleasesPage } from './pages/OfficeReleasesPage';
import { BillingProfilePage } from './pages/BillingProfilePage';
import { InvoiceDesignerPage } from './pages/InvoiceDesignerPage';
import { ClassroomPage } from './pages/ClassroomPage';
import { FamilyClassroomPage } from './pages/FamilyClassroomPage';
import { FamilySafetyPage } from './pages/FamilySafetyPage';
import { FeesPage } from './pages/FeesPage';
import { InstitutionsPage } from './pages/InstitutionsPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { AttendancePage } from './pages/AttendancePage';
import { AcademicsPage } from './pages/AcademicsPage';
import { EligibilityPage } from './pages/EligibilityPage';
import { SessionsPage } from './pages/SessionsPage';
import { AdminConsole } from './pages/AdminConsole';
import { ExamsPage } from './pages/ExamsPage';
import { FamilyPortal } from './pages/FamilyPortal';
import { PaymentsPage } from './pages/PaymentsPage';
import { FeeAdminPage } from './pages/FeeAdminPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { CommunicationPage } from './pages/CommunicationPage';
import { CertificatesPage } from './pages/CertificatesPage';
import { BrandingPage } from './pages/BrandingPage';
import { RecoveryPage } from './pages/RecoveryPage';
import { NewsAdminPage } from './pages/NewsAdminPage';
import { PublicSiteAdminPage } from './pages/PublicSiteAdminPage';
import { AutomationPage } from './pages/AutomationPage';
import { HolidaysAdminPage } from './pages/HolidaysAdminPage';
import { DownloadsAdminPage } from './pages/DownloadsAdminPage';
import { ReportsPage } from './pages/ReportsPage';
import { RegisterPage } from './pages/RegisterPage';
import { StudentImportPage } from './pages/StudentImportPage';
import { StaffImportPage } from './pages/StaffImportPage';
import { StaffPhotosPage } from './pages/StaffPhotosPage';
import { StaffIdCardsPage } from './pages/StaffIdCardsPage';
import { PhotoRollPage } from './pages/PhotoRollPage';
import { ScanAttendancePage } from './pages/ScanAttendancePage';
import { AttendanceSettingsPage } from './pages/AttendanceSettingsPage';
import { MasterDataPage } from './pages/MasterDataPage';
import { FaceConsentPage } from './pages/FaceConsentPage';
import { FamilyPermissionsPage } from './pages/FamilyPermissionsPage';
import { startPush } from './lib/native';
import { StudentPhotosPage } from './pages/StudentPhotosPage';
import { IdCardsPage } from './pages/IdCardsPage';
import { RemarksStaffPage } from './pages/RemarksStaffPage';
import { LabsPage } from './pages/LabsPage';
import { ReportCardsPage } from './pages/ReportCardsPage';
import { SportsPage } from './pages/SportsPage';
import { HousesLeadershipPage } from './pages/HousesLeadershipPage';
import { StorePage } from './pages/StorePage';
import { TripsPage } from './pages/TripsPage';
import { FeeAnalyticsPage } from './pages/FeeAnalyticsPage';
import { TrustRollupPage } from './pages/TrustRollupPage';
import { TransportPage } from './pages/TransportPage';
import { VehicleExpensesPage } from './pages/VehicleExpensesPage';
import { RequestsPage } from './pages/RequestsPage';
import { TimetableBuilderPage } from './pages/TimetableBuilderPage';
import { HrPage } from './pages/HrPage';
import { MyLeavePage } from './pages/MyLeavePage';
import { StaffAttendancePage } from './pages/StaffAttendancePage';
import { PayrollPage } from './pages/PayrollPage';

function FullScreenMessage({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div className="card" style={{ padding: 32, maxWidth: 420, textAlign: 'center' }}>
        <h1 className="h-display" style={{ fontSize: 22, marginBottom: 10 }}>{title}</h1>
        <p style={{ color: 'var(--ink-2)' }}>{body}</p>
      </div>
    </div>
  );
}

function Protected({ children, roles }: { children: ReactNode; roles?: readonly string[] }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenMessage title="Loading" body="One moment…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && user.role && !roles.includes(user.role)) {
    return (
      <AppShell>
        <div className="card" style={{ padding: 40, textAlign: 'center', maxWidth: 480, margin: '10vh auto' }}>
          <h2 className="h-display" style={{ fontSize: 22, marginBottom: 8 }}>No access</h2>
          <p style={{ color: 'var(--ink-2)' }}>
            Your role doesn’t have access to this area. If you think this is a mistake, ask your
            school administrator.
          </p>
        </div>
      </AppShell>
    );
  }
  // ⚠ THE GATE. Every authenticated route passes through Protected, so wrapping here means it
  // cannot be bypassed by typing a URL. (The server-side middleware is the real control — this is
  // so a human meets the notice instead of a wall of 451s.)
  return <ConsentGate><AppShell>{children}</AppShell></ConsentGate>;
}

function Gate() {
  const { loading, error, branding } = useTenant();

  // Public legal pages must work WITHOUT a tenant and without a session — app stores require
  // the deletion route to be reachable by anyone. Checked before tenant resolution.
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/terms') return <TermsPage />;
  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/delete-account') return <DeleteAccountPage />;

  // ⚠ DOCUMENT VERIFICATION, CHECKED BEFORE TENANT RESOLUTION.
  //
  // A receipt's QR may carry the ROOT domain — that is the fallback when a school hasn't set a
  // website on its billing profile. Without this check, `!resolveTenantSlug()` below sends every
  // such scan to the marketing landing page and the QR appears broken.
  //
  // It must also work with no tenant and no session: the person scanning is a bank, an employer or
  // a parent with no account, and the verify API is anonymous and cross-tenant by design.
  if (path === '/verify' || path.startsWith('/verify/')) return <VerifyRoutes />;

  // An explicit way to reach the marketing page from ANYWHERE — a tenant subdomain, a logged-in
  // session, anything. Without this there is no route to the landing page once you have a session,
  // which is precisely the situation when you most want it: standing in front of a school, on the
  // browser you demo from.
  if (path === '/home' || path === '/landing') return <LandingPage />;

  // No tenant in the URL at all (root domain / bare localhost) → this is a visitor,
  // not a school. Show the marketing landing page instead of an error.
  if (!resolveTenantSlug()) return <LandingPage />;

  if (loading) return <FullScreenMessage title="Loading" body="Preparing your workspace…" />;
  if (error || !branding) {
    return <FullScreenMessage title="Institution not found"
      body={error ?? 'This address isn’t linked to an institution yet.'} />;
  }

  // At the tenant ROOT path, a visitor with no session may be a prospective parent — show the
  // school's PUBLIC page if the school has enabled one. Any other path (/login, /fees, …), or an
  // existing session, goes straight to the app. The check is a quick fetch that resolves to the app
  // when there's no public page, so nothing is delayed for logged-in users navigating deep links.
  const atRoot = window.location.pathname === '/';
  const hasSession = !!tokens.access();
  if (atRoot && !hasSession) {
    return <PublicSiteOrApp />;
  }

  return <AppRoutes />;
}

/**
 * The verification routes, standing alone.
 *
 * ⚠ Deliberately OUTSIDE AuthProvider and I18nProvider. Neither is needed, and AuthProvider fires
 * a /api/auth/me on mount — which for an anonymous scanner is a guaranteed 401 in the console and
 * a pointless round-trip before the page they asked for renders.
 */
function VerifyRoutes() {
  return (
    <Routes>
      {/* ⚠ The bare /verify path matters now. The RECEIPT no longer prints the full URL — only
          the host — so somebody who cannot scan will type exactly this and must land somewhere
          useful rather than on a failure. */}
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/verify/:ref" element={<VerifyPage />} />
      <Route path="/verify/:kind/:ref" element={<VerifyPage />} />
    </Routes>
  );
}

/** Shows the public school page if one is enabled; otherwise the normal app. */
function PublicSiteOrApp() {
  const [decided, setDecided] = useState<'checking' | 'app'>('checking');
  if (decided === 'app') return <AppRoutes />;
  return <PublicSiteGate onNoSite={() => setDecided('app')} />;
}

/**
 * ⚠ INSIDE THE ROUTER, ON PURPOSE. Registering the FCM token needs a navigate() to hand to the
 * notification-tap listener, and useNavigate only works under a Router — a bootstrap in App() above
 * would compile and then throw the first time a parent tapped a notification.
 *
 * ⚠ AND IT RUNS ONCE PER LAUNCH, not once ever. FCM rotates tokens on its own schedule; an app that
 * registers only on first install goes silent the day it rotates.
 */
function NativeBootstrap() {
  const navigate = useNavigate();
  useEffect(() => {
    void startPush(
      (token, platform) => api.post('/api/devices', {
        token, platform,
        deviceLabel: navigator.userAgent.slice(0, 120),
        // ⚠ A literal, not a build-time define. Inventing a __APP_VERSION__ global would mean
        // touching the vite config for a field that is only ever read by a human debugging one
        // handset — and a define that is missing at runtime is a blank screen.
        appVersion: 'web',
      }),
      (path) => navigate(path),
    );
  }, [navigate]);
  return null;
}

function AppRoutes() {
  return (
    <AuthProvider>
      <I18nProvider>
      <NativeBootstrap />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* The driver's page. NO LOGIN — a driver is a contractor with a phone,
            not an Eduvora user. The trip token in the URL is the credential. */}
        <Route path="/drive/:token" element={<DriverPage />} />

        {/* ⚠ /verify is NOT routed here. Gate() intercepts it before tenant resolution — see the
            note there. Adding it back would be dead code that masks where it actually lives. */}

        <Route path="/" element={<Protected><OverviewPage /></Protected>} />
        <Route path="/ask" element={<Protected roles={ROLES.staff}><AskEduvoraPage /></Protected>} />
        <Route path="/at-risk" element={<Protected roles={ROLES.teaching}><StudentsAtRiskPage /></Protected>} />
        <Route path="/results" element={<Protected roles={ROLES.teaching}><ClassResultsPage /></Protected>} />
        {/* ⚠ ROLES.teaching, matching the `teaching` policy on /api/presence/day. The HR variant of
            the same screen is not a separate route — the page asks for the richer payload when the
            signed-in role allows it and falls back when it does not. */}
        <Route path="/presence" element={<Protected roles={ROLES.teaching}><PresencePage /></Protected>} />
        {/* ⚠ ROLES.teaching matches the `teaching` policy on /api/eligibility/watchlist. The
            leadership variant is not a second route — the page asks for the richer payload when
            the signed-in role is in `hr`, and falls back when it is not. */}
        <Route path="/exam-eligibility" element={<Protected roles={ROLES.teaching}><ExamEligibilityPage /></Protected>} />
        {/* ⚠ ROLES.hr, matching the `hr` policy on /api/staff/overview. The salary columns come
            from a second endpoint behind `tenant-admin`; the page asks for it only when the role
            allows and drops the columns when it does not. */}
        <Route path="/staff-overview" element={<Protected roles={ROLES.hr}><StaffOverviewPage /></Protected>} />
        <Route path="/invigilation" element={<Protected roles={ROLES.hr}><InvigilationPage /></Protected>} />
        {/* ⚠ ROLES.teaching, not ROLES.staff — see the note on QuestionBankEndpoints. The
            Accountant and the Transport Manager have logins and no business reading next
            month's questions. */}
        <Route path="/question-bank" element={<Protected roles={ROLES.teaching}><QuestionBankPage /></Protected>} />
        {/* The chapters questions are written FROM. Same policy as the bank itself: whoever may
            read next month's questions may read the material they were drawn out of. */}
        <Route path="/sources" element={<Protected roles={ROLES.teaching}><SourcesPage /></Protected>} />
        <Route path="/paper-builder" element={<Protected roles={ROLES.teaching}><PaperBuilderPage /></Protected>} />
        {/* ⚠ ROLES.teaching, matching the `teaching` policy on the blueprint routes. Retiring a
            pattern is behind `hr` on the server — the page hides that one button unless the
            signed-in role allows it, rather than the pattern editor being two routes. */}
        <Route path="/blueprints" element={<Protected roles={ROLES.teaching}><BlueprintEditorPage /></Protected>} />
        <Route path="/mark-entry" element={<Protected roles={ROLES.teaching}><MarkEntryRoute /></Protected>} />
        <Route path="/mastery" element={<Protected roles={ROLES.teaching}><MasteryRoute /></Protected>} />
        {/* Messages & AI usage. ⚠ ROLES.admin — TrustAdmin and CampusAdmin only. This screen
            is the school's own bill; a Principal or an Accountant has no need of it and a
            teacher certainly does not. And it is deliberately NOT behind a feature module:
            a tenant must not be able to switch off the page that shows what it has spent. */}
        <Route path="/usage" element={<Protected roles={ROLES.admin}><UsagePage /></Protected>} />
        {/* ⚠ ROLES.staff, not ROLES.hr. A teacher must be able to read their OWN duty; the strip
            renders nothing at all when they have none, so it costs an empty screen and no clutter. */}
        <Route path="/my-duties" element={<Protected roles={ROLES.staff}><MyDutiesPage /></Protected>} />
        <Route path="/students" element={<Protected roles={ROLES.roster}><StudentsPage /></Protected>} />
        {/* ⚠ staff, not admin — this is the class teacher's screen, on the class teacher's phone. */}
        <Route path="/attendance/photo-roll" element={<Protected roles={ROLES.roster}><PhotoRollPage /></Protected>} />
        <Route path="/attendance/scan" element={<Protected roles={ROLES.roster}><ScanAttendancePage /></Protected>} />
        {/* ⚠ ADMIN, NOT STAFF. These switches change how every teacher in the school takes a
            register, and the percentages decide who is reported short of attendance. */}
        <Route path="/attendance/settings" element={<Protected roles={ROLES.admin}><AttendanceSettingsPage /></Protected>} />
        {/* ⚠ ADMIN, matching every endpoint behind it. Renaming a subject, closing a class or
            retiring a department reaches every screen in the product; a subject teacher must not be
            able to do it between periods. */}
        <Route path="/master-data" element={<Protected roles={ROLES.admin}><MasterDataPage /></Protected>} />
        {/* ⚠ ADMIN, matching the API. A register of what families have agreed to about their
            children's biometrics is not something every teacher browses. */}
        <Route path="/attendance/face-consent" element={<Protected roles={ROLES.admin}><FaceConsentPage /></Protected>} />
        <Route path="/students/import" element={<Protected roles={ROLES.admin}   /* ⚠ admin only: this creates hundreds of records in one action and can delete them again */><StudentImportPage /></Protected>} />
        <Route path="/students/photos" element={<Protected roles={ROLES.admin}><StudentPhotosPage /></Protected>} />
        {/* ⚠ admin, not hr: an import can create and then delete the entire staff list, including
            people whose payroll the school depends on. Wider than the HR role is meant to carry. */}
        <Route path="/staff/import" element={<Protected roles={ROLES.admin}><StaffImportPage /></Protected>} />
        <Route path="/staff/photos" element={<Protected roles={ROLES.admin}><StaffPhotosPage /></Protected>} />
        <Route path="/staff/id-cards" element={<Protected roles={ROLES.admin}><StaffIdCardsPage /></Protected>} />
        <Route path="/students/id-cards" element={<Protected roles={ROLES.admin}><IdCardsPage /></Protected>} />
        <Route path="/admissions" element={<Protected roles={ROLES.frontdesk}><AdmissionsPage /></Protected>} />
        <Route path="/enquiries" element={<Protected roles={ROLES.frontdesk}><AdmissionsFunnelPage /></Protected>} />
        <Route path="/announcements" element={<Protected roles={ROLES.frontdesk}><AnnouncementsPage /></Protected>} />
        <Route path="/feed" element={<Protected roles={ROLES.all}><FeedPage /></Protected>} />
        <Route path="/library" element={<Protected roles={ROLES.library}><LibraryPage /></Protected>} />
        <Route path="/hostel" element={<Protected roles={ROLES.hostel}><HostelPage /></Protected>} />
        <Route path="/gate" element={<Protected roles={ROLES.gate}><GatePage /></Protected>} />
        <Route path="/releases" element={<Protected roles={ROLES.admin}><OfficeReleasesPage /></Protected>} />
        <Route path="/billing-profile" element={<Protected roles={ROLES.admin}><BillingProfilePage /></Protected>} />
        <Route path="/invoice-designer" element={<Protected roles={ROLES.admin}><InvoiceDesignerPage /></Protected>} />
        <Route path="/classroom" element={<Protected roles={ROLES.teaching}><ClassroomPage /></Protected>} />
        <Route path="/homework" element={<Protected roles={ROLES.familyAll}><FamilyClassroomPage /></Protected>} />
        <Route path="/fees" element={<Protected roles={ROLES.finance}><FeesPage /></Protected>} />
        <Route path="/attendance" element={<Protected roles={ROLES.roster}><AttendancePage /></Protected>} />
        <Route path="/academics" element={<Protected roles={ROLES.teaching}><AcademicsPage /></Protected>} />
        <Route path="/eligibility" element={<Protected roles={ROLES.teaching}><EligibilityPage /></Protected>} />
        <Route path="/sessions" element={<Protected roles={ROLES.teaching}><SessionsPage /></Protected>} />
        <Route path="/privacy-notice" element={<Protected roles={ROLES.admin}><PrivacyNoticePage /></Protected>} />
        <Route path="/admin" element={<Protected roles={ROLES.admin}><AdminConsole /></Protected>} />
        <Route path="/exams" element={<Protected roles={ROLES.teaching}><ExamsPage /></Protected>} />
        <Route path="/family" element={<Protected roles={ROLES.family}><FamilyPortal /></Protected>} />
        <Route path="/payments" element={<Protected roles={ROLES.family}><PaymentsPage /></Protected>} />
        <Route path="/bus-and-pickup" element={<Protected roles={ROLES.familyAll}><FamilySafetyPage /></Protected>} />
        <Route path="/permissions" element={<Protected roles={ROLES.family}><FamilyPermissionsPage /></Protected>} />
        <Route path="/fee-admin" element={<Protected roles={ROLES.finance}><FeeAdminPage /></Protected>} />
        <Route path="/approvals" element={<Protected roles={ROLES.finance}><ApprovalsPage /></Protected>} />
        <Route path="/communication" element={<Protected roles={ROLES.frontdesk}><CommunicationPage /></Protected>} />
        <Route path="/certificates" element={<Protected roles={ROLES.admin}><CertificatesPage /></Protected>} />
        <Route path="/branding" element={<Protected roles={ROLES.admin}><BrandingPage /></Protected>} />
        <Route path="/recovery" element={<Protected roles={ROLES.finance}><RecoveryPage /></Protected>} />
        <Route path="/news" element={<Protected roles={ROLES.frontdesk}><NewsAdminPage /></Protected>} />
        <Route path="/public-page" element={<Protected roles={ROLES.admin}><PublicSiteAdminPage /></Protected>} />
        <Route path="/automation" element={<Protected roles={ROLES.admin}><AutomationPage /></Protected>} />
        <Route path="/holidays" element={<Protected roles={ROLES.admin}><HolidaysAdminPage /></Protected>} />
        <Route path="/downloads-admin" element={<Protected roles={ROLES.admin}><DownloadsAdminPage /></Protected>} />
        {/* ⚠ ROLES.staff, matching the `staff` policy on /api/exports. One route for every caller:
            the page renders whatever the server's catalogue returns for the signed-in role — seven
            reports for an admin, one for a teacher. Deliberately NOT in a feature module, like
            /usage: a school must not be able to switch off the exports of its own records. */}
        <Route path="/reports" element={<Protected roles={ROLES.staff}><ReportsPage /></Protected>} />
        <Route path="/reports/register" element={<Protected roles={ROLES.staff}><RegisterPage /></Protected>} />
        <Route path="/remarks" element={<Protected roles={ROLES.teaching}><RemarksStaffPage /></Protected>} />
        <Route path="/labs" element={<Protected roles={ROLES.labs}><LabsPage /></Protected>} />
        <Route path="/report-cards" element={<Protected roles={ROLES.teaching}><ReportCardsPage /></Protected>} />
        <Route path="/sports" element={<Protected roles={ROLES.sports}><SportsPage /></Protected>} />
        <Route path="/houses" element={<Protected roles={ROLES.sports}><HousesLeadershipPage /></Protected>} />
        <Route path="/store" element={<Protected roles={ROLES.store}><StorePage /></Protected>} />
        <Route path="/trips" element={<Protected roles={ROLES.staff}><TripsPage /></Protected>} />
        <Route path="/fee-analytics" element={<Protected roles={ROLES.finance}><FeeAnalyticsPage /></Protected>} />
        <Route path="/all-campuses" element={<Protected roles={ROLES.trust}><TrustRollupPage /></Protected>} />
        <Route path="/transport" element={<Protected roles={ROLES.transportMgmt}><TransportPage /></Protected>} />
        <Route path="/vehicle-costs" element={<Protected roles={ROLES.transportMgmt}><VehicleExpensesPage /></Protected>} />
        <Route path="/requests" element={<Protected roles={ROLES.staff}><RequestsPage /></Protected>} />
        <Route path="/timetable" element={<Protected roles={ROLES.teaching}><TimetableBuilderPage /></Protected>} />
        <Route path="/hr" element={<Protected roles={ROLES.hr}><HrPage /></Protected>} />
        <Route path="/my-leave" element={<Protected roles={ROLES.staff}><MyLeavePage /></Protected>} />
        <Route path="/staff-attendance" element={<Protected roles={ROLES.hr}><StaffAttendancePage /></Protected>} />
        <Route path="/payroll" element={<Protected roles={ROLES.admin}><PayrollPage /></Protected>} />
        <Route path="/structure" element={<Protected roles={ROLES.admin}><InstitutionsPage /></Protected>} />
        <Route path="/templates" element={<Protected roles={ROLES.admin}><TemplatesPage /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </I18nProvider>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <TenantProvider>
          <Gate />
        </TenantProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

/* ⚠ The paper is chosen with a query string rather than a path segment, because both screens are
   reached from a link on the papers list and neither is meaningful without one. Reading it here
   keeps the pages themselves free of router knowledge and testable in isolation. */
function MarkEntryRoute() {
  const [params] = useSearchParams();
  return <MarkEntryPage paperId={params.get('paperId') ?? undefined} />;
}

function MasteryRoute() {
  const [params] = useSearchParams();
  return (
    <MasteryPage
      paperId={params.get('paperId') ?? undefined}
      academicClassId={params.get('classId') ?? undefined}
    />
  );
}
